(ns minorodata.components.schedule.core
  (:require [cljs.spec.alpha :as spec]
            [goog.string :as g.string]
            [goog.string.format]
            [minorodata.app.utils :as utils]
            [re-posh.core :as re-posh]
            [spec-tools.core :as st]
            [ui.button :refer [button]]
            [ui.typography :as typography]
            [ui.utils :refer [clsx]]))

(spec/def ::cron
  (st/spec string?))

(spec/def ::schedule-form
  (spec/and
   (spec/keys :req-un [::cron])
   (st/spec
    (fn [{:keys [cron]}]
      (not (utils/cron-empty-field? cron :hour)))
    {:reason "You must specify hours"})
   (st/spec
    (fn [{:keys [cron]}]
      (not (utils/cron-empty-field? cron :minute)))
    {:reason "You must specify minutes"})))

(defn required-label []
  [:span {:class "text-kleene-text-error"} "*"])


(defn day-of-week-cron [{:keys [field disabled required]}]
  (let [{:keys [action value]} field]
    [:section
     [typography/label "Days of the week" (when required [required-label])]

     (into
      [:div {:class "flex flex-row divide-x divide-solid border rounded-t"}]
      (for [n    (concat (next utils/days-range-cron) [0])
            :let [selected? (utils/in-cron? value :dow n)]]
        ^{:key n}
        [button {:variant (if selected? :primary :empty-transparent)
                 :class "flex-1 rounded-none"
                 :wrapper-class "text-center"
                 :data-cy  (keyword (str "days-select-" n))
                 :disabled disabled
                 :on-click #(action (utils/update-cron value :dow n))}
         (get utils/days-of-the-week-short n)]))

     (into
      [:div {:class "flex flex-row divide-x divide-solid border border-t-0 rounded-b"}]
      (for [[label data-cy day-range] [["Every day" "days-select-all" utils/days-range]
                                       ["Weekdays" "days-select-weekday" utils/weekdays-range]]
            :let [selected? (= day-range (utils/get-cron-field value :dow))]]
        [button {:variant (if selected? :primary :empty)
                 :class "flex-1 rounded-none"
                 :wrapper-class "text-center"
                 :data-cy  (keyword data-cy)
                 :disabled disabled
                 :on-click #(action (utils/update-cron value :dow day-range true))}
         label]))]))

(defn hour-cron [{:keys [field disabled required]}]
  (let [{:keys [action value]} field]
    [:section
     [typography/label "Hours" (when required [required-label])]

     (into
      [:div {:class "grid grid-cols-8 rounded-xs border divide-x divide-y"}]
      (for [n utils/hours-range
            :let [selected? (utils/in-cron? value :hour n)]]
        ^{:key n}
        [button {:variant (if selected? :primary :empty-transparent)
                 :class (clsx "flex-1 rounded-none"
                              {"border-t-0!" (<= 0 n 7)
                               "border-l-0!" (#{8 16} n)})
                 :wrapper-class "text-center"
                 :data-cy  (keyword (str "hour-select-" n))
                 :disabled disabled
                 :on-click #(action (utils/update-cron value :hour n))}
         n]))]))

(defn minute-cron [{:keys [field disabled required]}]
  (let [{:keys [action value]} field
        extended? @(re-posh/subscribe [:unleash/is-enabled "fe.full-cron-schedule"])]
    [:section
     [typography/label "Minutes" (when required [required-label])]

     (into
      [:div {:class "flex flex-row divide-x divide-solid border rounded-xs overflow-auto"}]
      (for [n    utils/minutes-range
            :let [selected?  (utils/in-cron? value :minute n)
                  next-value (cond extended? n
                                   selected? ["*"]
                                   :else     [n])]]
        ^{:key n}
        [button {:variant (if selected? :primary :empty-transparent)
                 :class "flex-1 rounded-none"
                 :wrapper-class "text-center"
                 :data-cy  (keyword (str "minute-select-" n))
                 :disabled disabled
                 :on-click #(action (utils/update-cron value :minute next-value (not extended?)))}
         (g.string/format "%02d" n)]))]))

(def month-short-names ["Jan" "Feb" "Mar" "Apr" "May" "Jun" "Jul" "Aug" "Sep" "Oct" "Nov" "Dec"])

(defn month-cron [{:keys [field disabled]}]
  (let [{:keys [action value]} field]
    [:section
     [typography/label "Months"]

     (into
      [:div {:class "flex flex-row divide-x divide-solid border rounded-xs overflow-auto"}]
      (for [n    utils/months-range
            :let [selected? (utils/in-cron? value :month n)]]
        ^{:key n}
        [button {:variant (if selected? :primary :empty-transparent)
                 :class (clsx "flex-1 rounded-none px-2")
                 :wrapper-class "text-center"
                 :data-cy  (keyword (str "month-select-" n))
                 :disabled disabled
                 :on-click #(action (utils/update-cron value :month n))}
         (nth month-short-names (dec n))]))]))

(defn days-of-month-cron [{:keys [field disabled]}]
  (let [{:keys [action value]} field]
    [:section
     [typography/label "Days of month"]

     (into
      [:div {:class "grid grid-cols-7 rounded-xs"}]
      (for [n    utils/days-of-month-range
            :let [selected? (utils/in-cron? value :dom n)]]
        ^{:key n}
        [button {:variant (if selected? :primary :empty-transparent)
                 :class (clsx "flex-1 rounded-none border-t border-l"
                              {"rounded-tl" (= 1 n)
                               "rounded-tr" (= 7 n)
                               "rounded-bl" (= 29 n)
                               "rounded-br!" (#{28 31} n)
                               "border-b" (>= n 25)
                               "border-r" (#{7 14 21 28 31} n)})
                 :wrapper-class "text-center"
                 :data-cy  (keyword (str "days-of-month-select-" n))
                 :disabled disabled
                 :on-click #(action (utils/update-cron value :dom n))}
         (g.string/format "%02d" n)]))]))

(defn cron-editor [{:keys [field disabled]}]
  (let [extended? @(re-posh/subscribe [:unleash/is-enabled "fe.full-cron-schedule"])
        cron-map  (utils/cron->map (:value field))
        dow?      (not= (:dow cron-map) ["*"])
        dom?      (not= (:dom cron-map) ["*"])]
    [:div {:class "flex flex-col gap-4"}
     (when extended?
       [month-cron {:field field :disabled disabled}])
     [day-of-week-cron {:field field :disabled (or disabled dom?) :required (not extended?)}]
     (when extended?
       [:<>
        [:div {:class "w-full h-px bg-kleene-layout-border flex items-center justify-center"}
         [:div {:class "bg-kleene-background border border-kleene-layout-border rounded-full px-2 py-px"}
          "or"]]
        [days-of-month-cron {:field field :disabled (or disabled dow?)}]])
     [hour-cron {:field field :disabled disabled :required true}]
     [minute-cron {:field field :disabled disabled :required true}]]))
